export type AttributionPayload = {
  firstTouchChannel: string | null;
  firstTouchSource: string | null;
  lastTouchChannel: string | null;
  lastTouchSource: string | null;
};

const FIRST_TOUCH_STORAGE_KEY = "solmi_first_touch_attribution_v1";
const LAST_TOUCH_STORAGE_KEY = "solmi_last_touch_attribution_v1";
const POSTHOG_ATTRIBUTION_EVENT_KEY = "solmi_posthog_attribution_event_v1";
const GCLID_STORAGE_KEY = "solmi_gclid_v1";
// Google Ads click identifiers persist for 90 days for offline conversion uploads.
const GCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const ATTRIBUTION_VERSION = "2026-06-17";

type StoredGclid = {
  gclid: string;
  capturedAt: number;
};

type TouchAttribution = {
  channel: string;
  source: string;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  adsAccount: string | null;
  referrer: string | null;
  landingPath: string | null;
  landingUrl: string | null;
  clickIdType: string | null;
  capturedAt: number;
};

type DerivedTouchAttribution = {
  touch: TouchAttribution;
  hasCampaignSignal: boolean;
};

type TouchAttributionState = {
  firstTouch: TouchAttribution;
  lastTouch: TouchAttribution;
  currentTouch: TouchAttribution;
  currentHasCampaignSignal: boolean;
};

type PostHogAttributionValue = string | number | boolean;
export type PostHogAttributionProperties = Record<
  string,
  PostHogAttributionValue
>;

export type PostHogAttributionClient = {
  register?: (properties: Record<string, unknown>) => void;
  capture?: (
    eventName: string,
    properties?: Record<string, unknown>,
  ) => unknown;
};

const SEARCH_ENGINES = [
  "google.",
  "bing.",
  "yahoo.",
  "duckduckgo.",
  "baidu.",
  "yandex.",
];
const SOCIAL_SOURCES = [
  "facebook",
  "instagram",
  "x",
  "twitter",
  "linkedin",
  "tiktok",
  "reddit",
  "youtube",
  "pinterest",
  "snapchat",
];
const PAID_MEDIUMS = [
  "cpc",
  "ppc",
  "paid",
  "paid_search",
  "paidsearch",
  "sem",
  "display",
  "search",
  "search_ads",
  "searchads",
];
const GOOGLE_ADS_SOURCES = [
  "adwords",
  "google_adwords",
  "google_ads",
  "googleads",
];
// Hosts we redirect *through* mid-session — OAuth providers on sign-in and
// payment providers on checkout. These are internal hops, not acquisition
// sources. Without excluding them the return leg of every Google sign-in lands
// with document.referrer = accounts.google.com, which matches the "google."
// SEARCH_ENGINES prefix and rewrites last-touch to organic_search — destroying
// the real channel (paid_search / referral / direct) the user arrived on.
const INTERNAL_REFERRER_HOSTS = [
  "accounts.google.com",
  "accounts.youtube.com",
  "appleid.apple.com",
  "idmsa.apple.com",
  "pay.google.com",
];
// Suffix-matched, so every subdomain counts. Enumerating Stripe hosts missed
// `billing.stripe.com` (the customer billing portal), which showed up as a
// `referral` touch on a real signup after the first pass of this fix — the
// portal is as much an internal hop as checkout is, and Stripe has more
// subdomains than are worth chasing one at a time. Own domains are here too: a
// full page load from an internal link is a self-referral, never a new touch.
const INTERNAL_REFERRER_DOMAINS = [
  "stripe.com",
  "solmi.ai",
  "songmakerai.ai",
];

const sanitizeString = (
  value: unknown,
  maxLength = 128,
  lowercase = true,
): string | null => {
  if (typeof value !== "string") return null;
  const cleaned = value
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, maxLength);
  if (!cleaned) return null;
  return lowercase ? cleaned.toLowerCase() : cleaned;
};

const normalize = (value: unknown): string | null => {
  return sanitizeString(value, 128, true);
};

const normalizeUrlValue = (value: unknown): string | null => {
  return sanitizeString(value, 512, false);
};

const normalizeMatchKey = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const key = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  return key.length > 0 ? key : null;
};

const normalizeClickId = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getLocalStorage = (): Storage | null => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    return null;
  }
  return null;
};

const getSessionStorage = (): Storage | null => {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      return window.sessionStorage;
    }
    if (typeof sessionStorage !== "undefined") return sessionStorage;
  } catch {
    return null;
  }
  return null;
};

const getCurrentUrl = (): URL | null => {
  if (typeof window === "undefined") return null;
  try {
    return new URL(window.location.href);
  } catch {
    return null;
  }
};

const matchesDomain = (host: string, domain: string): boolean =>
  host === domain || host.endsWith(`.${domain}`);

const getCurrentHost = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return normalize(window.location.hostname);
  } catch {
    return null;
  }
};

/**
 * True for referrers that are an internal hop rather than a real acquisition
 * source: our own hosts (including the live origin, so preview deploys and
 * localhost are covered without hardcoding) and the OAuth/payment providers we
 * redirect through.
 */
const isInternalReferrer = (host: string | null): boolean => {
  if (!host) return false;
  if (INTERNAL_REFERRER_HOSTS.includes(host)) return true;
  if (INTERNAL_REFERRER_DOMAINS.some((domain) => matchesDomain(host, domain))) {
    return true;
  }
  const currentHost = getCurrentHost();
  return Boolean(currentHost && host === currentHost);
};

const parseReferrerHost = (): string | null => {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    const host = normalize(new URL(document.referrer).hostname);
    // Drop internal hops so the touch falls back to "direct", which keeps
    // getTouchAttributionState from overwriting the stored last touch.
    return isInternalReferrer(host) ? null : host;
  } catch {
    return null;
  }
};

const isSocial = (value: string | null): boolean => {
  if (!value) return false;
  return SOCIAL_SOURCES.some((source) => value.includes(source));
};

const isSearchEngine = (value: string | null): boolean => {
  if (!value) return false;
  return SEARCH_ENGINES.some((engine) => value.includes(engine));
};

const isPaidMedium = (value: string | null): boolean => {
  const key = normalizeMatchKey(value);
  return Boolean(key && PAID_MEDIUMS.includes(key));
};

const isGoogleAdsSource = (value: string | null): boolean => {
  const key = normalizeMatchKey(value);
  return Boolean(key && GOOGLE_ADS_SOURCES.includes(key));
};

const getGoogleClickId = (params: URLSearchParams): string | null =>
  normalizeClickId(params.get("gclid")) ??
  normalizeClickId(params.get("gbraid")) ??
  normalizeClickId(params.get("wbraid"));

const getGoogleClickIdType = (params: URLSearchParams): string | null => {
  if (normalizeClickId(params.get("gclid"))) return "gclid";
  if (normalizeClickId(params.get("gbraid"))) return "gbraid";
  if (normalizeClickId(params.get("wbraid"))) return "wbraid";
  return null;
};

const deriveTouchAttribution = (): DerivedTouchAttribution => {
  if (typeof window === "undefined") {
    return {
      touch: {
        channel: "direct",
        source: "direct",
        medium: null,
        campaign: null,
        term: null,
        content: null,
        utmSource: null,
        utmMedium: null,
        adsAccount: null,
        referrer: null,
        landingPath: null,
        landingUrl: null,
        clickIdType: null,
        capturedAt: Date.now(),
      },
      hasCampaignSignal: false,
    };
  }

  const currentUrl = getCurrentUrl();
  const params =
    currentUrl?.searchParams ?? new URLSearchParams(window.location.search);
  const utmSource = normalize(params.get("utm_source"));
  const utmMedium = normalize(params.get("utm_medium"));
  const utmCampaign = normalize(params.get("utm_campaign"));
  const utmTerm = normalize(params.get("utm_term"));
  const utmContent = normalize(params.get("utm_content"));
  const adsAccount =
    normalize(params.get("utm_account")) ??
    normalize(params.get("utm_ad_account")) ??
    normalize(params.get("ads_account"));
  const googleClickId = getGoogleClickId(params);
  const fbclid = normalizeClickId(params.get("fbclid"));
  const ttclid = normalizeClickId(params.get("ttclid"));
  const referrerHost = parseReferrerHost();
  const clickIdType =
    getGoogleClickIdType(params) ??
    (fbclid ? "fbclid" : ttclid ? "ttclid" : null);

  let channel = "direct";
  let source = "direct";

  if (
    googleClickId ||
    isGoogleAdsSource(utmSource) ||
    isPaidMedium(utmMedium)
  ) {
    channel = "paid_search";
    source = utmSource ?? "google_ads";
  } else if (fbclid || ttclid) {
    channel = "social";
    source = utmSource ?? (fbclid ? "facebook" : "tiktok");
  } else if (utmSource && isSocial(utmSource)) {
    channel = "social";
    source = utmSource;
  } else if (referrerHost && isSocial(referrerHost)) {
    channel = "social";
    source = referrerHost;
  } else if (referrerHost && isSearchEngine(referrerHost)) {
    channel = "organic_search";
    source = utmSource ?? referrerHost;
  } else if (utmSource) {
    channel = "referral";
    source = utmSource;
  } else if (referrerHost) {
    channel = "referral";
    source = referrerHost;
  }

  return {
    touch: {
      channel,
      source,
      medium: utmMedium,
      campaign: utmCampaign,
      term: utmTerm,
      content: utmContent,
      utmSource,
      utmMedium,
      adsAccount,
      referrer: referrerHost,
      landingPath: currentUrl
        ? `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
        : null,
      landingUrl: currentUrl?.href ?? null,
      clickIdType,
      capturedAt: Date.now(),
    },
    hasCampaignSignal: Boolean(
      utmSource ||
        utmMedium ||
        utmCampaign ||
        utmTerm ||
        utmContent ||
        adsAccount ||
        googleClickId ||
        fbclid ||
        ttclid,
    ),
  };
};

const readStoredTouch = (storageKey: string): TouchAttribution | null => {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TouchAttribution>;
    const channel = normalize(parsed.channel);
    const source = normalize(parsed.source);
    if (!channel || !source) return null;
    return {
      channel,
      source,
      medium: normalize(parsed.medium),
      campaign: normalize(parsed.campaign),
      term: normalize(parsed.term),
      content: normalize(parsed.content),
      utmSource: normalize(parsed.utmSource),
      utmMedium: normalize(parsed.utmMedium),
      adsAccount: normalize(parsed.adsAccount),
      referrer: normalize(parsed.referrer),
      landingPath: normalizeUrlValue(parsed.landingPath),
      landingUrl: normalizeUrlValue(parsed.landingUrl),
      clickIdType: normalize(parsed.clickIdType),
      capturedAt:
        typeof parsed.capturedAt === "number" ? parsed.capturedAt : Date.now(),
    };
  } catch {
    return null;
  }
};

const writeStoredTouch = (storageKey: string, touch: TouchAttribution) => {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey, JSON.stringify(touch));
  } catch {
    // ignore storage write errors (private mode/quota)
  }
};

const getTouchAttributionState = (): TouchAttributionState => {
  const current = deriveTouchAttribution();
  const storedFirstTouch = readStoredTouch(FIRST_TOUCH_STORAGE_KEY);
  const firstTouch = storedFirstTouch ?? current.touch;

  if (!storedFirstTouch) {
    writeStoredTouch(FIRST_TOUCH_STORAGE_KEY, firstTouch);
  }

  const storedLastTouch = readStoredTouch(LAST_TOUCH_STORAGE_KEY);
  const shouldUseCurrentAsLast =
    current.hasCampaignSignal ||
    current.touch.channel !== "direct" ||
    !storedLastTouch;
  const lastTouch = shouldUseCurrentAsLast ? current.touch : storedLastTouch;

  if (shouldUseCurrentAsLast) {
    writeStoredTouch(LAST_TOUCH_STORAGE_KEY, lastTouch);
  }

  return {
    firstTouch,
    lastTouch,
    currentTouch: current.touch,
    currentHasCampaignSignal: current.hasCampaignSignal,
  };
};

const getAdsAccount = (touch: TouchAttribution): string | null => {
  if (touch.adsAccount) return touch.adsAccount;
  const sourceKey = normalizeMatchKey(touch.utmSource ?? touch.source);
  const mediumKey = normalizeMatchKey(touch.utmMedium ?? touch.medium);
  if (!sourceKey || !GOOGLE_ADS_SOURCES.includes(sourceKey)) return null;
  if (mediumKey === "search_ads" || mediumKey === "search") {
    return "google_ads_search_ads";
  }
  return "google_ads";
};

const compactProperties = (
  properties: Record<string, PostHogAttributionValue | null | undefined>,
): PostHogAttributionProperties => {
  const compacted: PostHogAttributionProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined || value === "") continue;
    compacted[key] = value;
  }
  return compacted;
};

const buildPostHogAttributionProperties = (
  firstTouch: TouchAttribution,
  lastTouch: TouchAttribution,
): PostHogAttributionProperties => {
  const adsAccount = getAdsAccount(lastTouch) ?? getAdsAccount(firstTouch);
  return compactProperties({
    attribution_version: ATTRIBUTION_VERSION,
    first_touch_channel: firstTouch.channel,
    first_touch_source: firstTouch.source,
    first_touch_medium: firstTouch.medium,
    first_touch_campaign: firstTouch.campaign,
    first_touch_referrer: firstTouch.referrer,
    first_touch_landing_path: firstTouch.landingPath,
    first_touch_landing_url: firstTouch.landingUrl,
    last_touch_channel: lastTouch.channel,
    last_touch_source: lastTouch.source,
    last_touch_medium: lastTouch.medium,
    last_touch_campaign: lastTouch.campaign,
    last_touch_referrer: lastTouch.referrer,
    last_touch_landing_path: lastTouch.landingPath,
    last_touch_landing_url: lastTouch.landingUrl,
    traffic_channel: lastTouch.channel,
    traffic_source: lastTouch.source,
    traffic_medium: lastTouch.medium,
    utm_source: lastTouch.utmSource,
    utm_medium: lastTouch.utmMedium,
    utm_campaign: lastTouch.campaign,
    utm_term: lastTouch.term,
    utm_content: lastTouch.content,
    utm_account: lastTouch.adsAccount,
    ads_account: adsAccount,
    is_google_ads_search_ads: adsAccount === "google_ads_search_ads",
  });
};

const shouldCapturePostHogAttributionEvent = (
  eventName: string,
  touch: TouchAttribution,
): boolean => {
  const storage = getSessionStorage();
  if (!storage) return true;

  const dedupeKey = [
    eventName,
    touch.utmSource,
    touch.utmMedium,
    touch.campaign,
    touch.term,
    touch.content,
    touch.adsAccount,
    touch.clickIdType,
    touch.landingUrl,
  ]
    .filter(Boolean)
    .join("|");
  if (!dedupeKey) return false;

  try {
    if (storage.getItem(POSTHOG_ATTRIBUTION_EVENT_KEY) === dedupeKey) {
      return false;
    }
    storage.setItem(POSTHOG_ATTRIBUTION_EVENT_KEY, dedupeKey);
    return true;
  } catch {
    return true;
  }
};

export const getAttributionPayload = (): AttributionPayload => {
  const { firstTouch, lastTouch } = getTouchAttributionState();

  return {
    firstTouchChannel: firstTouch.channel,
    firstTouchSource: firstTouch.source,
    lastTouchChannel: lastTouch.channel,
    lastTouchSource: lastTouch.source,
  };
};

export const getPostHogAttributionProperties =
  (): PostHogAttributionProperties => {
    const { firstTouch, lastTouch } = getTouchAttributionState();
    return buildPostHogAttributionProperties(firstTouch, lastTouch);
  };

export const syncPostHogAttribution = (
  posthog: PostHogAttributionClient | null | undefined,
): void => {
  if (!posthog) return;
  try {
    const state = getTouchAttributionState();
    const properties = buildPostHogAttributionProperties(
      state.firstTouch,
      state.lastTouch,
    );
    posthog.register?.(properties);

    if (!state.currentHasCampaignSignal) return;
    const currentAdsAccount = getAdsAccount(state.currentTouch);
    const eventName = currentAdsAccount
      ? "ads_traffic_detected"
      : "marketing_attribution_captured";
    if (shouldCapturePostHogAttributionEvent(eventName, state.currentTouch)) {
      posthog.capture?.(eventName, {
        ...properties,
        current_ads_account: currentAdsAccount ?? undefined,
        current_utm_source: state.currentTouch.utmSource ?? undefined,
        current_utm_medium: state.currentTouch.utmMedium ?? undefined,
        current_utm_campaign: state.currentTouch.campaign ?? undefined,
        current_landing_url: state.currentTouch.landingUrl ?? undefined,
      });
    }
  } catch {
    // Analytics attribution must never affect app boot or navigation.
  }
};

/**
 * Capture Google Ads click identifier from the current URL (?gclid=, ?gbraid=,
 * ?wbraid=) and persist it. Idempotent — only the first hit per session writes.
 * Server-side conversion uploads need this to attribute purchases back to the
 * specific click; without it Google falls back to email-based matching, which
 * is weaker.
 */
export const captureGclidFromUrl = (): void => {
  if (typeof window === "undefined") return;
  const currentUrl = getCurrentUrl();
  const params =
    currentUrl?.searchParams ?? new URLSearchParams(window.location.search);
  const gclid = getGoogleClickId(params);
  if (!gclid) return;
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    const existing = storage.getItem(GCLID_STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing) as Partial<StoredGclid>;
      if (
        parsed.gclid &&
        parsed.capturedAt &&
        Date.now() - parsed.capturedAt < GCLID_TTL_MS
      ) {
        return;
      }
    }
    const payload: StoredGclid = { gclid, capturedAt: Date.now() };
    storage.setItem(GCLID_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode / quota) — non-fatal.
  }
};

/** Returns the stored gclid if present and within the 90-day TTL, else null. */
export const getStoredGclid = (): string | null => {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(GCLID_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredGclid>;
    if (!parsed.gclid || !parsed.capturedAt) return null;
    if (Date.now() - parsed.capturedAt > GCLID_TTL_MS) {
      storage.removeItem(GCLID_STORAGE_KEY);
      return null;
    }
    return parsed.gclid;
  } catch {
    return null;
  }
};
